import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useSearchParams } from 'react-router-dom';

const StripeConnectSettings: React.FC = () => {
  const { shop } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<any>(null);
  const [hasConnectionError, setHasConnectionError] = useState(false);

  useEffect(() => {
    // URLパラメータからの成功/リフレッシュメッセージを処理
    if (searchParams.get('success') === 'true') {
      setSuccess('Stripeアカウントの設定が完了しました！ステータスを更新しています...');
      handleRefreshStatus();
    } else if (searchParams.get('refresh') === 'true') {
      setError('Stripeアカウントの設定が中断されました。もう一度お試しください。');
    }
  }, [searchParams]);

  useEffect(() => {
    if (shop?.stripeConnectAccountId) {
      handleRefreshStatus();
    }
  }, [shop]);

  const handleCreateAccount = async () => {
    if (!shop) return;

    setLoading(true);
    setError(null);

    try {
      const createAccount = httpsCallable(functions, 'createStripeConnectAccount');
      const result = await createAccount({ shopId: shop.id }) as any;

      // オンボーディングURLにリダイレクト
      window.location.href = result.data.onboardingUrl;
    } catch (err: any) {
      console.error('Error creating Stripe account:', err);
      setError(err.message || 'Stripeアカウントの作成に失敗しました');
      setLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    if (!shop) return;

    setLoading(true);
    setError(null);

    try {
      const createLink = httpsCallable(functions, 'createStripeAccountLink');
      const result = await createLink({ shopId: shop.id }) as any;

      // オンボーディングURLにリダイレクト
      window.location.href = result.data.onboardingUrl;
    } catch (err: any) {
      console.error('Error creating account link:', err);
      setError(err.message || 'アカウントリンクの作成に失敗しました');
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!shop?.stripeConnectAccountId) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshStatus = httpsCallable(functions, 'refreshStripeConnectStatus');
      const result = await refreshStatus({ shopId: shop.id }) as any;

      setAccountStatus(result.data);

      if (result.data.status === 'active') {
        setSuccess('Stripeアカウントが有効化されています。決済を受け付けることができます。');
      } else if (result.data.status === 'pending') {
        if (!result.data.onboardingCompleted) {
          setError('Stripeアカウントの設定が完了していません。「設定を続ける」ボタンから設定を完了してください。');
        } else {
          setError('Stripeアカウントは審査中です。通常1-2営業日以内に完了します。');
        }
      } else if (result.data.status === 'restricted') {
        setError('Stripeアカウントに制限がかかっています。Stripeダッシュボードで詳細を確認してください。');
      }
    } catch (err: any) {
      console.error('Error refreshing status:', err);
      setError(err.message || 'ステータスの更新に失敗しました');
      // Stripeアカウントが存在しない場合はリセットを促す
      if (err.message?.includes('does not exist') || err.message?.includes('not connected') || err.message?.includes('access')) {
        setHasConnectionError(true);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleReset = async () => {
    if (!shop) return;

    setResetting(true);
    setError(null);

    try {
      const reset = httpsCallable(functions, 'resetStripeConnect');
      await reset({ shopId: shop.id });

      setSuccess('Stripe Connect設定をリセットしました。新しくアカウントを連携できます。');
      setShowResetDialog(false);
      setHasConnectionError(false);

      // ページをリロードして店舗情報を更新
      window.location.reload();
    } catch (err: any) {
      console.error('Error resetting Stripe Connect:', err);
      setError(err.message || 'リセットに失敗しました');
    } finally {
      setResetting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!shop) return;

    setDisconnecting(true);
    setError(null);

    try {
      const disconnect = httpsCallable(functions, 'disconnectStripeConnect');
      await disconnect({ shopId: shop.id });

      setSuccess('Stripe Connectの連携を解除しました。再度連携する場合は、新しくアカウントを作成してください。');
      setShowDisconnectDialog(false);
      setAccountStatus(null);

      // ページをリロードして店舗情報を更新
      window.location.reload();
    } catch (err: any) {
      console.error('Error disconnecting Stripe:', err);
      setError(err.message || '連携解除に失敗しました');
    } finally {
      setDisconnecting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'restricted':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return '有効';
      case 'pending':
        return '保留中';
      case 'restricted':
        return '制限あり';
      default:
        return '不明';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Stripe Connect 設定
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Stripeアカウントを連携して、予約の事前決済機能を有効化します
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
          {hasConnectionError && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={() => setShowResetDialog(true)}
              >
                設定をリセットして再連携
              </Button>
            </Box>
          )}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Stripe Connect について
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CreditCardIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="事前決済機能"
              secondary="予約時にクレジットカード決済を受け付けることができます"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <AccountBalanceIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="直接振込"
              secondary="決済金額は手数料を差し引いた後、直接あなたの銀行口座に振り込まれます"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <InfoIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="プラットフォーム手数料: 0円"
              secondary="Stripeの決済手数料（3.6%）のみが発生します"
            />
          </ListItem>
        </List>
      </Paper>

      {!shop?.stripeConnectAccountId ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Stripeアカウントを連携する
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              まず最初に、Stripeアカウントを作成して連携する必要があります。
              「アカウントを作成する」ボタンをクリックすると、Stripeのオンボーディング画面に移動します。
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleCreateAccount}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AccountBalanceIcon />}
            >
              {loading ? '作成中...' : 'Stripeアカウントを作成する'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Stripeアカウント情報
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefreshStatus}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={20} /> : 'ステータスを更新'}
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                アカウントID
              </Typography>
              <Typography variant="body1" fontFamily="monospace">
                {shop.stripeConnectAccountId}
              </Typography>
            </Box>

            {accountStatus && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ステータス
                  </Typography>
                  <Chip
                    label={getStatusLabel(accountStatus.status)}
                    color={getStatusColor(accountStatus.status) as any}
                    icon={
                      accountStatus.status === 'active' ? (
                        <CheckCircleIcon />
                      ) : (
                        <ErrorIcon />
                      )
                    }
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    機能
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="決済受付"
                      color={accountStatus.chargesEnabled ? 'success' : 'default'}
                      size="small"
                      icon={accountStatus.chargesEnabled ? <CheckCircleIcon /> : undefined}
                    />
                    <Chip
                      label="振込"
                      color={accountStatus.payoutsEnabled ? 'success' : 'default'}
                      size="small"
                      icon={accountStatus.payoutsEnabled ? <CheckCircleIcon /> : undefined}
                    />
                    <Chip
                      label="オンボーディング完了"
                      color={accountStatus.onboardingCompleted ? 'success' : 'default'}
                      size="small"
                      icon={accountStatus.onboardingCompleted ? <CheckCircleIcon /> : undefined}
                    />
                  </Box>
                </Box>

                {accountStatus.requirements && accountStatus.requirements.currently_due?.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                      以下の情報が必要です:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {accountStatus.requirements.currently_due.map((req: string, index: number) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {shop.stripeConnectStatus !== 'active' && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleContinueOnboarding}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AccountBalanceIcon />}
                >
                  {loading ? '読み込み中...' : '設定を続ける'}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={() => setShowDisconnectDialog(true)}
                disabled={disconnecting}
              >
                連携を解除
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 連携解除確認ダイアログ */}
      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Stripe Connect 連携解除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stripe Connectの連携を解除しますか？
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            連携を解除すると、以下の影響があります：
            <ul style={{ marginBottom: 0 }}>
              <li>事前決済機能が無効になります</li>
              <li>決済が必要なメニューの予約ができなくなります</li>
              <li>Stripeアカウント自体は削除されません（Stripeダッシュボードから管理できます）</li>
              <li>再度連携する場合は、同じアカウントまたは新しいアカウントで設定できます</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)} disabled={disconnecting}>
            キャンセル
          </Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={disconnecting}
            startIcon={disconnecting ? <CircularProgress size={20} /> : undefined}
          >
            {disconnecting ? '解除中...' : '連携を解除する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* リセット確認ダイアログ */}
      <Dialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Stripe Connect 設定をリセット</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stripe Connectの設定をリセットしますか？
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            リセットすると、保存されている接続情報が削除され、新しくStripeアカウントを連携できるようになります。
            <ul style={{ marginBottom: 0 }}>
              <li>現在のStripe接続情報がクリアされます</li>
              <li>新しいアカウントで再設定できます</li>
              <li>Stripeアカウント自体は削除されません</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetDialog(false)} disabled={resetting}>
            キャンセル
          </Button>
          <Button
            onClick={handleReset}
            color="primary"
            variant="contained"
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {resetting ? 'リセット中...' : 'リセットする'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StripeConnectSettings;
